import { PlanoResumo, ProdutoPlano } from '../../models/parametros/plano.model';
import { extractIsoDate } from '../date-format.util';
import { normalizarTexto } from '../texto.util';

/**
 * Texto da opção dos campos "Código do Plano": o id do produto (o que a parametrização grava), a descrição e
 * o código do plano no Amadeus (NUM_PLBNF) entre parênteses. A busca percorre esse texto, então acha por qualquer um deles.
 * Produto encerrado leva " - encerrado", para o admin ver antes de escolher.
 */
export function rotuloProduto(produto: ProdutoPlano): string {
  const encerrado = produto.encerrado ? ' - encerrado' : '';
  return `${produto.id} - ${produto.descricao} (${produto.codigo})${encerrado}`;
}

/**
 * Opção do `vivest-autocomplete` dos campos "Código do Plano". O valor é o próprio rótulo, não o id: o
 * componente troca o texto digitado pelo rótulo da opção cujo valor seja igual a ele, então com o id
 * digitar "2" mostraria o rótulo do produto 2 e o campo ficaria preso a ele.
 */
export function opcaoProduto(produto: ProdutoPlano): { value: string; label: string } {
  const rotulo = rotuloProduto(produto);
  return { value: rotulo, label: rotulo };
}

/** Produto da opção escolhida no autocomplete (o valor é o rótulo, ver `opcaoProduto`). */
export function produtoDaOpcao(produtos: readonly ProdutoPlano[], valor: string | undefined): ProdutoPlano | null {
  return produtos.find((produto) => rotuloProduto(produto) === valor) ?? null;
}

/**
 * Produto encerrado é o de DT_FIM anterior a hoje. Compara a data textual (`yyyy-MM-dd`), como a API,
 * que trata DT_FIM igual a hoje como vigente.
 */
export function produtoEncerrado(dtFim: string | null | undefined, hoje = hojeIso()): boolean {
  const fim = extractIsoDate(dtFim);
  return fim !== '' && fim < hoje;
}

function hojeIso(): string {
  const agora = new Date();
  const dois = (n: number) => String(n).padStart(2, '0');
  return `${agora.getFullYear()}-${dois(agora.getMonth() + 1)}-${dois(agora.getDate())}`;
}

/** Produtos que podem receber uma parametrização nova: com código no Amadeus e ainda sem parametrização. */
export function produtosLivres(produtos: ProdutoPlano[]): ProdutoPlano[] {
  return produtos.filter((produto) => produto.codigo !== '' && produto.idPlanoParam === null);
}

/** Filtros da tela "Parâmetros de Plano". Texto vazio e status `'todos'` não filtram. */
export interface FiltroPlanos {
  codigo: string;
  nome: string;
  status: PlanoResumo['status'] | 'todos';
}

export const FILTRO_PLANOS_VAZIO: FiltroPlanos = { codigo: '', nome: '', status: 'todos' };

export type ColunaOrdenacaoPlanos = 'id' | 'codigo' | 'nome' | 'status' | 'ultimaAtualizacao';
export type DirecaoOrdenacao = 'asc' | 'desc';

export interface OrdenacaoPlanos {
  coluna: ColunaOrdenacaoPlanos;
  direcao: DirecaoOrdenacao;
}

/**
 * Código e nome por "contém", sem diferenciar caixa e acento (ex.: "pl0" encontra "PL001"); status
 * por igualdade.
 */
export function filtrarPlanos(planos: readonly PlanoResumo[], filtro: FiltroPlanos): PlanoResumo[] {
  const codigo = normalizarTexto(filtro.codigo);
  const nome = normalizarTexto(filtro.nome);
  return planos.filter(
    (plano) =>
      (!codigo || normalizarTexto(plano.codigo).includes(codigo)) &&
      (!nome || normalizarTexto(plano.nome).includes(nome)) &&
      (filtro.status === 'todos' || plano.status === filtro.status),
  );
}

/**
 * Ordena sem alterar a lista original. Textos sem diferenciar caixa e acento, com os números dentro
 * deles em ordem numérica ("PL2" antes de "PL10"). Datas são ISO (`yyyy-MM-dd`), então a comparação
 * textual equivale à cronológica.
 * Valores vazios ficam sempre no fim, em qualquer direção.
 */
export function ordenarPlanos(planos: readonly PlanoResumo[], ordenacao: OrdenacaoPlanos): PlanoResumo[] {
  const fator = ordenacao.direcao === 'asc' ? 1 : -1;
  return [...planos].sort((a, b) => {
    const valorA = a[ordenacao.coluna];
    const valorB = b[ordenacao.coluna];
    const vazioA = valorA === '';
    const vazioB = valorB === '';
    if (vazioA || vazioB) {
      return Number(vazioA) - Number(vazioB);
    }
    const comparacao =
      typeof valorA === 'number' && typeof valorB === 'number'
        ? valorA - valorB
        : String(valorA).localeCompare(String(valorB), 'pt-BR', { sensitivity: 'base', numeric: true });
    return comparacao * fator;
  });
}

/** Itens da página pedida (páginas começam em 1). */
export function paginar<T>(itens: readonly T[], pagina: number, tamanho: number): T[] {
  const inicio = (pagina - 1) * tamanho;
  return itens.slice(inicio, inicio + tamanho);
}

export function totalPaginas(totalItens: number, tamanho: number): number {
  return Math.max(1, Math.ceil(totalItens / tamanho));
}

/**
 * Números de página exibidos na paginação: no máximo `limite` páginas seguidas, com a atual no
 * meio sempre que possível (ex.: página 7 de 20 com limite 5 → 5, 6, 7, 8, 9).
 */
export function janelaPaginas(atual: number, total: number, limite = 5): number[] {
  const tamanho = Math.min(limite, total);
  const inicio = Math.min(Math.max(1, atual - Math.floor(tamanho / 2)), total - tamanho + 1);
  return Array.from({ length: tamanho }, (_, i) => inicio + i);
}
